import type { RefObject } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CROSSFADE_SECS, PRE_FADE_HOLD_VH } from './animationConstants';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface Args {
    sectionRef: RefObject<HTMLElement | null>;
}

export const useCardsAnimation = ({ sectionRef }: Args) => {
    useGSAP(
        () => {
            const wrappers = gsap.utils.toArray<HTMLElement>('[data-card-wrapper]', sectionRef.current);

            wrappers.forEach((wrapper) => {
                const card = wrapper.querySelector<HTMLElement>('[data-testimonial-card]')!;
                const hover = card.querySelector<HTMLElement>('[data-hover-layer]')!;
                const bg = card.querySelector<HTMLElement>('[data-bg]')!;
                const reveals = Array.from(card.querySelectorAll<HTMLElement>('[data-reveal]'));

                gsap.set(hover, { opacity: 0 });
                gsap.set(bg, { scale: 1 });
                gsap.set(reveals, { opacity: 0 });

                const crossfadeTl = gsap
                    .timeline({ paused: true })
                    .to(hover, { opacity: 1, ease: 'power1.inOut', duration: CROSSFADE_SECS })
                    .to(bg, { scale: 1.04, ease: 'power1.inOut', duration: CROSSFADE_SECS }, '<')
                    .to(reveals, { opacity: 1, ease: 'power1.inOut', duration: CROSSFADE_SECS }, '<');

                let busy = false;
                let pendingDir = 0;

                const startForward = () => {
                    if (crossfadeTl.progress() >= 1) {
                        return;
                    }
                    busy = true;
                    crossfadeTl.play();
                };
                const startReverse = () => {
                    if (crossfadeTl.progress() <= 0) {
                        return;
                    }
                    busy = true;
                    crossfadeTl.reverse();
                };
                const release = () => {
                    busy = false;
                    if (pendingDir !== 0) {
                        const d = pendingDir;
                        pendingDir = 0;
                        if (d > 0) {
                            startForward();
                        } else {
                            startReverse();
                        }
                    }
                };

                crossfadeTl.eventCallback('onComplete', release);
                crossfadeTl.eventCallback('onReverseComplete', release);

                const preFadePx = (PRE_FADE_HOLD_VH / 100) * window.innerHeight;

                ScrollTrigger.create({
                    trigger: wrapper,
                    start: `top+=${preFadePx}px top`,
                    onEnter: () => {
                        if (!busy) {
                            startForward();
                        } else if (crossfadeTl.reversed()) {
                            pendingDir = 1;
                        }
                    },
                    onLeaveBack: () => {
                        if (!busy) {
                            startReverse();
                        } else if (!crossfadeTl.reversed()) {
                            pendingDir = -1;
                        }
                    },
                });
            });
        },
        { scope: sectionRef }
    );
};
